"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Circle, Cloud, Loader2, X } from "lucide-react";
import { AIRBRIDGE_PROJECTS, type AirbridgeProject } from "@/lib/airbridge-config";

type FetchStatus = "idle" | "requesting" | "polling" | "downloading" | "processing" | "error";

function getStepState(status: FetchStatus, step: 1 | 2 | 3): "pending" | "active" | "done" {
    const current = ({ requesting: 1, polling: 1, downloading: 2, processing: 3 } as Record<string, number>)[status] ?? 0;
    if (current > step) return "done";
    if (current === step) return "active";
    return "pending";
}

interface AirbridgeFetchPanelProps {
    onDataLoaded: (csvText: string, displayName: string) => void;
    onFileSaved?: () => void;
    disabled?: boolean;
}

function getDefaultDates() {
    const now = new Date();
    // KST = UTC+9
    const kstOffset = 9 * 60 * 60 * 1000;
    const kstNow = new Date(now.getTime() + kstOffset);
    const endDate = kstNow.toISOString().split("T")[0];

    const weekAgo = new Date(kstNow.getTime() - 7 * 24 * 60 * 60 * 1000);
    const startDate = weekAgo.toISOString().split("T")[0];

    return { startDate, endDate };
}

export function AirbridgeFetchPanel({ onDataLoaded, onFileSaved, disabled }: AirbridgeFetchPanelProps) {
    const defaults = getDefaultDates();
    const [selectedProject, setSelectedProject] = useState<string>("");
    const [startDate, setStartDate] = useState(defaults.startDate);
    const [endDate, setEndDate] = useState(defaults.endDate);
    const [status, setStatus] = useState<FetchStatus>("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const cancelRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    const startTimer = () => {
        setElapsedSeconds(0);
        timerRef.current = setInterval(() => {
            setElapsedSeconds(prev => prev + 1);
        }, 1000);
    };

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const handleCancel = () => {
        cancelRef.current = true;
        stopTimer();
        setStatus("idle");
    };

    const handleFetch = async () => {
        if (!selectedProject || !startDate || !endDate) return;

        cancelRef.current = false;
        setErrorMessage("");
        setStatus("requesting");
        startTimer();

        try {
            // 1. Export 요청
            const postRes = await fetch("/api/airbridge/export", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    projectId: selectedProject,
                    startDate,
                    endDate,
                }),
            });

            if (!postRes.ok) {
                const err = await postRes.json();
                throw new Error(err.error || "Export 요청 실패");
            }

            const { reportId } = await postRes.json();

            // 2. Polling (2초 간격, 최대 150회 = 5분)
            setStatus("polling");

            for (let i = 0; i < 150; i++) {
                if (cancelRef.current) return;

                // 첫 번째는 3초, 이후는 2초 간격
                await new Promise(r => setTimeout(r, i === 0 ? 3000 : 2000));

                if (cancelRef.current) return;

                const pollRes = await fetch(
                    `/api/airbridge/export?projectId=${selectedProject}&reportId=${reportId}`
                );

                if (!pollRes.ok) {
                    const err = await pollRes.json();
                    throw new Error(err.error || "상태 확인 실패");
                }

                const pollData = await pollRes.json();

                if (pollData.status === "completed") {
                    // 3. CSV 다운로드 (상태 확인과 분리된 별도 요청)
                    setStatus("downloading");

                    const dlRes = await fetch(
                        `/api/airbridge/export?downloadUrl=${encodeURIComponent(pollData.downloadUrl)}`
                    );

                    if (!dlRes.ok) {
                        const err = await dlRes.json();
                        throw new Error(err.error || "CSV 다운로드 실패");
                    }

                    const dlData = await dlRes.json();
                    stopTimer();

                    const project = AIRBRIDGE_PROJECTS.find(p => p.projectId === selectedProject);
                    const displayName = `${project?.displayName || selectedProject} (${startDate} ~ ${endDate})`;

                    // 데이터 관리에 자동 저장 (fire-and-forget)
                    const csvFileName = `${project?.appName || selectedProject}_${startDate}_${endDate}.csv`;
                    const formData = new FormData();
                    formData.append('file', new File([dlData.csvData], csvFileName, { type: 'text/csv' }));
                    fetch('/api/data-files', { method: 'POST', body: formData })
                        .then(() => onFileSaved?.())
                        .catch(() => {});

                    setStatus("processing");

                    // React가 "processing" 상태를 렌더링할 수 있도록 양보
                    await new Promise(r => setTimeout(r, 0));
                    if (cancelRef.current) return;

                    onDataLoaded(dlData.csvData, displayName);
                    setStatus("idle");
                    return;
                }
            }

            throw new Error("내보내기 시간 초과 (5분). 다시 시도해주세요.");
        } catch (error: unknown) {
            if (cancelRef.current) return;
            stopTimer();
            setStatus("error");
            setErrorMessage(error instanceof Error ? error.message : "알 수 없는 오류");
        }
    };

    const isActive = status !== "idle" && status !== "error";
    const project = AIRBRIDGE_PROJECTS.find(p => p.projectId === selectedProject);

    return (
        <div className="space-y-4 p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-300">
                <Cloud className="w-4 h-4 text-blue-400" />
                Airbridge 데이터 가져오기
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* 프로젝트 선택 */}
                <Select
                    value={selectedProject}
                    onValueChange={setSelectedProject}
                    disabled={isActive || disabled}
                >
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                        <SelectValue placeholder="프로젝트 선택" />
                    </SelectTrigger>
                    <SelectContent>
                        {AIRBRIDGE_PROJECTS.map((p) => (
                            <SelectItem key={p.projectId} value={p.projectId}>
                                {p.displayName}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {/* 시작일 */}
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={isActive || disabled}
                    className="px-3 py-2 rounded-md bg-slate-900 border border-slate-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />

                {/* 종료일 */}
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={isActive || disabled}
                    className="px-3 py-2 rounded-md bg-slate-900 border border-slate-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
            </div>

            {/* 버튼 및 상태 */}
            <div className="flex items-center gap-3">
                {isActive ? (
                    <div className="w-full space-y-1.5">
                        {([
                            { step: 1 as const, label: "Airbridge 추출 중", extra: (status === "requesting" || status === "polling") ? `${elapsedSeconds}초 경과` : undefined },
                            { step: 2 as const, label: "CSV 다운로드 중", extra: undefined },
                            { step: 3 as const, label: "대시보드 생성 중", extra: undefined },
                        ]).map(({ step, label, extra }) => {
                            const state = getStepState(status, step);
                            return (
                                <div key={step} className="flex items-center gap-2 text-sm">
                                    {state === "done"
                                        ? <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                                        : state === "active"
                                        ? <Loader2 className="w-4 h-4 animate-spin text-blue-400 shrink-0" />
                                        : <Circle className="w-4 h-4 text-slate-600 shrink-0" />
                                    }
                                    <span className={state === "done" ? "text-slate-500" : state === "active" ? "text-slate-200 font-medium" : "text-slate-600"}>
                                        {label}
                                    </span>
                                    {extra && <span className="text-xs text-slate-400">({extra})</span>}
                                </div>
                            );
                        })}
                        {status !== "processing" && (
                            <Button variant="ghost" size="sm" onClick={handleCancel} className="text-slate-400 hover:text-white h-auto py-1 px-1 mt-0.5">
                                <X className="w-3 h-3 mr-1" />취소
                            </Button>
                        )}
                    </div>
                ) : (
                    <Button
                        onClick={handleFetch}
                        disabled={!selectedProject || !startDate || !endDate || disabled}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        <Cloud className="w-4 h-4 mr-2" />
                        데이터 가져오기
                    </Button>
                )}
            </div>

            {/* 에러 표시 */}
            {status === "error" && errorMessage && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md p-3">
                    {errorMessage}
                    <button
                        onClick={() => { setStatus("idle"); setErrorMessage(""); }}
                        className="ml-2 underline hover:text-red-300"
                    >
                        다시 시도
                    </button>
                </div>
            )}
        </div>
    );
}
